// bill_subsystem_v1 + invoice_crud_v1 + optional_price_v1 + commission_tiered_v1 + reference_amount_v1: tiered commission via reference_amount input
import React, { useState, useEffect } from 'react';
import API from '../api';

const PAYMENT_METHODS = [
  { value: '', label: '— ไม่ระบุ —' },
  { value: 'cash', label: '💵 เงินสด' },
  { value: 'bank', label: '🏦 โอนธนาคาร' },
  { value: 'promptpay', label: '📱 พร้อมเพย์' },
  { value: 'crypto', label: '🪙 Crypto (USDT)' },
];

function emptyItem() {
  return { description: '', quantity: 1, unit_price: 0, product_id: '', reference_amount: '' };
}

/* commission_tiered_v1 + reference_amount_v1: calculate commission from reference_amount input */
function calcCommission(referenceAmount, product) {
  if (!product) return 0;
  var rate = Number(product.commission_rate || 0);
  if (rate <= 0) return 0;
  var type = product.commission_type || 'percent';
  var minCharge = Number(product.commission_min_charge || 0);
  var minBase = Number(product.commission_min_base || 0);
  var maxBase = Number(product.commission_max_base || 0);
  var refAmt = Number(referenceAmount || 0);

  // No reference amount = no commission yet
  if (refAmt <= 0) return 0;

  // Below min_base = no commission
  if (minBase > 0 && refAmt < minBase) return 0;

  var calculated = type === 'fixed' ? rate : (refAmt * rate / 100);

  // Above max_base = use calculated as-is (no floor)
  if (maxBase > 0 && refAmt >= maxBase) return calculated;

  // Between min_base and max_base = apply min_charge floor
  if (minCharge > 0 && calculated < minCharge) return minCharge;
  return calculated;
}

function emptyForm() {
  return {
    tenant_customer_id: '',
    recipient_name: '',
    recipient_phone: '',
    recipient_address: '',
    currency: 'THB',
    issue_date: (function () { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); })(), /* invoice_issue_date_local_v1: ใช้วันที่ท้องถิ่น (ไทย) ไม่ใช่ UTC */
    due_date: '',
    items: [emptyItem()],
    payment_method: '',
    bank_account_id: '',
    crypto_wallet_address: '',
    crypto_network: '',
    usdt_rate: '',
    note: '',
  };
}

export default function BillInvoiceForm(props) {
  const invoice = props.invoice;
  const onClose = props.onClose;
  const [form, setForm] = useState(emptyForm());
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /* backdrop_close_fix_v1: ปิดโมดัลเฉพาะเมื่อกด+ปล่อยบน backdrop จริงๆ (กันเด้งปิดตอนลากเลือกข้อความในช่องกรอก) */
  const overlayMouseDown = React.useRef(false);

  // Load reference data
  useEffect(function () {
    API.get('/api/customers').then(function (r) { setCustomers(r.data || []); }).catch(function () {});
    API.get('/api/products?active=true').then(function (r) { setProducts(r.data || []); }).catch(function () {});
    API.get('/api/bank-accounts').then(function (r) { setBankAccounts(r.data || []); }).catch(function () { setBankAccounts([]); });
    /* usdt_default_rate_v1: auto-fill USDT rate for new invoice (not edit) */
    if (!invoice) {
      API.get('/api/bill/settings/usdt-rate')
        .then(function (r) {
          var rate = r.data && r.data.rate;
          if (rate) {
            setForm(function (f) { return f.usdt_rate ? f : Object.assign({}, f, { usdt_rate: String(rate) }); });
          }
        })
        .catch(function () {});
      /* usdt_default_wallet_v1: auto-fill wallet + network (บิลใหม่) */
      API.get('/api/bill/settings/usdt-wallet')
        .then(function (r) {
          var w = r.data && r.data.wallet;
          var n = r.data && r.data.network;
          setForm(function (f) {
            var nf = f;
            if (w && !f.crypto_wallet_address) { nf = Object.assign({}, nf, { crypto_wallet_address: String(w) }); }
            if (n && !nf.crypto_network) { nf = Object.assign({}, nf, { crypto_network: String(n) }); }
            return nf;
          });
        })
        .catch(function () {});
    }
  }, []);

  // Load existing invoice for edit
  useEffect(function () {
    if (!invoice) return;
    setLoading(true);
    API.get('/api/invoices/' + invoice.id)
      .then(function (r) {
        var inv = r.data.invoice || r.data;
        var items = r.data.items || [];
        setForm({
          tenant_customer_id: inv.tenant_customer_id || '',
          recipient_name: inv.recipient_name || '',
          recipient_phone: inv.recipient_phone || '',
          recipient_address: inv.recipient_address || '',
          currency: inv.currency || 'THB',
          issue_date: (inv.issue_date || '').slice(0, 10),
          due_date: inv.due_date ? String(inv.due_date).slice(0, 10) : '',
          items: items.length > 0 ? items.map(function (i) {
            return { description: i.description, quantity: Number(i.quantity), unit_price: Number(i.unit_price), product_id: '', reference_amount: i.reference_amount ? String(i.reference_amount) : '' };
          }) : [emptyItem()],
          payment_method: inv.payment_method || '',
          bank_account_id: inv.bank_account_id || '',
          crypto_wallet_address: inv.crypto_wallet_address || '',
          crypto_network: inv.crypto_network || '',
          usdt_rate: inv.usdt_rate ? String(inv.usdt_rate) : '',
          note: inv.note || '',
        });
      })
      .catch(function (err) {
        setError((err.response && err.response.data && err.response.data.error) || 'โหลดบิลไม่สำเร็จ');
      })
      .finally(function () { setLoading(false); });
  }, [invoice]);

  function updateField(name, value) {
    setForm(function (f) {
      var next = Object.assign({}, f);
      next[name] = value;
      return next;
    });
  }

  function pickCustomer(customerId) {
    updateField('tenant_customer_id', customerId);
    if (!customerId) return;
    var c = customers.find(function (x) { return x.id === customerId; });
    if (!c) return;
    setForm(function (f) {
      return Object.assign({}, f, {
        tenant_customer_id: customerId,
        recipient_name: f.recipient_name || c.name,
        recipient_phone: f.recipient_phone || c.phone || '',
        recipient_address: f.recipient_address || c.address || '',
      });
    });
  }

  function updateItem(idx, name, value) {
    /* reference_amount_v1: simpler — no cross-row recompute since commission uses reference_amount input */
    setForm(function (f) {
      var items = f.items.slice();
      items[idx] = Object.assign({}, items[idx]);
      items[idx][name] = value;
      return Object.assign({}, f, { items: items });
    });
  }

  function pickProduct(idx, productId) {
    /* reference_amount_v1: commission row uses reference_amount input, regular row uses product price */
    if (!productId) { updateItem(idx, 'product_id', ''); return; }
    var p = products.find(function (x) { return x.id === productId; });
    if (!p) return;
    setForm(function (f) {
      var items = f.items.slice();
      var productRate = Number(p.commission_rate || 0);
      var productPrice = Number(p.unit_price || 0);
      var fillPrice;
      if (productRate > 0) {
        // Commission product — start with 0 until user enters reference_amount
        fillPrice = 0;
      } else if (productPrice > 0) {
        fillPrice = productPrice;
      } else {
        fillPrice = '';
      }
      items[idx] = Object.assign({}, items[idx], {
        product_id: productId,
        description: p.name,
        unit_price: fillPrice,
        quantity: productRate > 0 ? 1 : items[idx].quantity,
        reference_amount: productRate > 0 ? items[idx].reference_amount : '', /* clear if non-commission */
      });
      return Object.assign({}, f, { items: items });
    });
  }

  /* reference_amount_v1: when reference_amount changes on a commission row, recalc unit_price */
  function updateReferenceAmount(idx, value) {
    setForm(function (f) {
      var items = f.items.slice();
      items[idx] = Object.assign({}, items[idx], { reference_amount: value });
      var p = items[idx].product_id && products.find(function (x) { return x.id === items[idx].product_id; });
      if (p && Number(p.commission_rate || 0) > 0) {
        items[idx].unit_price = calcCommission(value, p);
      }
      return Object.assign({}, f, { items: items });
    });
  }

  function addItem() {
    setForm(function (f) { return Object.assign({}, f, { items: f.items.concat([emptyItem()]) }); });
  }

  function removeItem(idx) {
    setForm(function (f) {
      if (f.items.length <= 1) return f;
      var items = f.items.slice();
      items.splice(idx, 1);
      return Object.assign({}, f, { items: items });
    });
  }

  function calcLineTotal(it) {
    return Number(it.quantity) * Number(it.unit_price);
  }

  function calcSubtotal() {
    return form.items.reduce(function (s, it) { return s + calcLineTotal(it); }, 0);
  }

  async function handleSave() {
    setError('');
    if (!form.recipient_name.trim()) { setError('กรอกชื่อผู้รับ'); return; }
    var validItems = form.items.filter(function (it) {
      /* invoice_allow_negative_v1: unit_price ติดลบได้ (เครดิต) — เช็คแค่เป็นตัวเลข */
      return it.description.trim() && Number(it.quantity) > 0 && isFinite(Number(it.unit_price));
    });
    if (validItems.length === 0) { setError('ต้องมีรายการอย่างน้อย 1 ที่กรอกครบ'); return; }

    var payload = {
      tenant_customer_id: form.tenant_customer_id || null,
      recipient_name: form.recipient_name.trim(),
      recipient_phone: form.recipient_phone.trim() || null,
      recipient_address: form.recipient_address.trim() || null,
      currency: form.currency,
      issue_date: form.issue_date,
      due_date: form.due_date || null,
      items: validItems.map(function (it) {
        var refAmt = Number(it.reference_amount || 0);
        return {
          description: it.description.trim(),
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          reference_amount: refAmt > 0 ? refAmt : null,
        };
      }),
      payment_method: form.payment_method || null,
      bank_account_id: form.bank_account_id || null,
      crypto_wallet_address: form.crypto_wallet_address.trim() || null,
      crypto_network: form.crypto_network || null,
      usdt_rate: form.usdt_rate ? Number(form.usdt_rate) : null,
      note: form.note.trim() || null,
    };

    setSaving(true);
    try {
      if (invoice) {
        await API.put('/api/invoices/' + invoice.id, payload);
      } else {
        await API.post('/api/invoices', payload);
      }
      onClose(true);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onMouseDown={function (e) { overlayMouseDown.current = (e.target === e.currentTarget); }}
      onClick={function (e) { if (!saving && e.target === e.currentTarget && overlayMouseDown.current) onClose(false); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 100 }}>
      <div onClick={function (e) { e.stopPropagation(); }} style={{ background: 'white', borderRadius: 14, padding: 24, maxWidth: 720, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 16px' }}>{invoice ? 'แก้ไขใบแจ้งหนี้ ' + (invoice.invoice_number || '') : 'สร้างใบแจ้งหนี้'}</h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>กำลังโหลด...</div>
        ) : (
          <div>
            {/* Customer picker */}
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10, marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>ลูกค้า (เลือกเพื่อ auto-fill + ผูกบิล)</label>
              <select value={form.tenant_customer_id} onChange={function (e) { pickCustomer(e.target.value); }} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 6, fontFamily: 'inherit' }}>
                <option value="">— ไม่ผูกลูกค้า (กรอกชื่อผู้รับเอง) —</option>
                {customers.map(function (c) {
                  return <option key={c.id} value={c.id}>{c.name} {c.phone ? '(' + c.phone + ')' : ''}</option>;
                })}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>ชื่อผู้รับ <span style={{ color: '#dc2626' }}>*</span></label>
                <input value={form.recipient_name} onChange={function (e) { updateField('recipient_name', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>เบอร์</label>
                <input value={form.recipient_phone} onChange={function (e) { updateField('recipient_phone', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>ที่อยู่</label>
              <textarea value={form.recipient_address} onChange={function (e) { updateField('recipient_address', e.target.value); }} rows={2} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>สกุลเงิน</label>
                <select value={form.currency} onChange={function (e) { updateField('currency', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, fontFamily: 'inherit' }}>
                  <option value="THB">฿ THB</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>วันออกบิล</label>
                <input type="date" value={form.issue_date} onChange={function (e) { updateField('issue_date', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>ครบกำหนด</label>
                <input type="date" value={form.due_date} onChange={function (e) { updateField('due_date', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
            </div>

            {/* Items */}
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>📋 รายการ</label>
                <button onClick={addItem} style={{ background: 'white', border: '1px solid #10b981', color: '#10b981', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>+ เพิ่มรายการ</button>
              </div>

              {form.items.map(function (it, idx) {
                return (
                  <div key={idx} style={{ background: 'white', padding: 10, borderRadius: 8, marginBottom: 6, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 6 }}>
                      <select value={it.product_id} onChange={function (e) { pickProduct(idx, e.target.value); }} style={{ padding: 6, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }}>
                        <option value="">— เลือกสินค้า (optional) —</option>
                        {products.map(function (p) {
                          return <option key={p.id} value={p.id}>{p.name} — ฿{Number(p.unit_price).toLocaleString('th-TH')}</option>;
                        })}
                      </select>
                      {form.items.length > 1 ? <button onClick={function () { removeItem(idx); }} style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✕</button> : <span />}
                    </div>
                    <input value={it.description} onChange={function (e) { updateItem(idx, 'description', e.target.value); }} placeholder="รายละเอียดสินค้า/บริการ" style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
                    {(function(){ var prod = it.product_id && products.find(function(x){return x.id===it.product_id;}); var isComm = prod && Number(prod.commission_rate||0) > 0; if (!isComm) return null; return (
                      <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                        <label style={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>🎯 ยอดอ้างอิงสำหรับคำนวณ {Number(prod.commission_rate)}% (฿)</label>
                        <input type="number" min="0" step="0.01" value={it.reference_amount} onChange={function (e) { updateReferenceAmount(idx, e.target.value); }} placeholder="เช่น 800000" style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid #6ee7b7', fontSize: 13, marginTop: 3, boxSizing: 'border-box' }} />
                        <div style={{ fontSize: 10, color: '#065f46', marginTop: 3 }}>
                          {Number(prod.commission_min_base||0) > 0 ? 'เริ่มคิดเมื่อยอด ≥ ฿' + Number(prod.commission_min_base).toLocaleString('th-TH') : ''}
                          {Number(prod.commission_min_charge||0) > 0 ? ' / ขั้นต่ำ ฿' + Number(prod.commission_min_charge).toLocaleString('th-TH') : ''}
                          {Number(prod.commission_max_base||0) > 0 ? ' / เกิน ฿' + Number(prod.commission_max_base).toLocaleString('th-TH') + ' ตามจริง' : ''}
                        </div>
                      </div>
                    ); })()}
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: 6, alignItems: 'center' }}>
                      <input type="number" min="0" step="1" value={it.quantity} onChange={function (e) { updateItem(idx, 'quantity', e.target.value); }} placeholder="จำนวน" style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                      <input type="number" min="0" step="0.01" value={it.unit_price} onChange={function (e) { updateItem(idx, 'unit_price', e.target.value); }} placeholder="ราคา/หน่วย" style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                      <div style={{ textAlign: 'right', fontSize: 13, color: '#475569', fontWeight: 600 }}>= {Number(calcLineTotal(it)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                );
              })}

              <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 700, marginTop: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
                รวม: {form.currency === 'USDT' ? Number(calcSubtotal()).toLocaleString('th-TH') + ' USDT' : '฿' + Number(calcSubtotal()).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </div>
              {/* invoice_usdt_preview_v1: USDT equivalent preview */}
              {(function () {
                var rate = Number(form.usdt_rate);
                var sub = Number(calcSubtotal());
                if (!(rate > 0) || !(sub > 0)) return null;
                if (form.currency === 'USDT') {
                  var thbEq = sub * rate;
                  return (
                    <div style={{ textAlign: 'right', fontSize: 13, color: '#64748b', marginTop: 2 }}>
                      ≈ ฿{thbEq.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span style={{ color: '#94a3b8' }}>(@ {rate.toLocaleString('th-TH')})</span>
                    </div>
                  );
                }
                var usdtEq = sub / rate;
                return (
                  <div style={{ textAlign: 'right', fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    ≈ {usdtEq.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT <span style={{ color: '#94a3b8' }}>(@ {rate.toLocaleString('th-TH')})</span>
                  </div>
                );
              })()}
            </div>

            {/* Payment method */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>วิธีชำระเงิน</label>
              <select value={form.payment_method} onChange={function (e) { updateField('payment_method', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, fontFamily: 'inherit' }}>
                {PAYMENT_METHODS.map(function (pm) { return <option key={pm.value} value={pm.value}>{pm.label}</option>; })}
              </select>
            </div>

            {(form.payment_method === 'bank' || form.payment_method === 'promptpay') && bankAccounts.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, color: '#475569' }}>บัญชีรับเงิน</label>
                <select value={form.bank_account_id} onChange={function (e) { updateField('bank_account_id', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, fontFamily: 'inherit' }}>
                  <option value="">— เลือกบัญชี —</option>
                  {bankAccounts.map(function (ba) {
                    return <option key={ba.id} value={ba.id}>{ba.bank_name || ba.account_name} {ba.account_no ? '(' + ba.account_no + ')' : ''}</option>;
                  })}
                </select>
              </div>
            )}

            {form.payment_method === 'crypto' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 13, color: '#475569' }}>Wallet Address</label>
                  <input value={form.crypto_wallet_address} onChange={function (e) { updateField('crypto_wallet_address', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, marginTop: 4, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, color: '#475569' }}>Network</label>
                  <select value={form.crypto_network} onChange={function (e) { updateField('crypto_network', e.target.value); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, fontFamily: 'inherit' }}>
                    <option value="">—</option>
                    <option value="TRC20">TRC20</option>
                    <option value="ERC20">ERC20</option>
                    <option value="BEP20">BEP20</option>
                  </select>
                </div>
              </div>
            )}

            {/* invoice_usdt_preview_v1: แสดง USDT rate ทุก currency */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>เรท USDT (1 USDT = ? THB)</label>
              <input type="number" min="0" step="0.01" value={form.usdt_rate} onChange={function (e) { updateField('usdt_rate', e.target.value); }} placeholder="เช่น 36.50" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>หมายเหตุ</label>
              <textarea value={form.note} onChange={function (e) { updateField('note', e.target.value); }} rows={2} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>⚠️ {error}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={function () { onClose(false); }} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: 14 }}>ยกเลิก</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : '#10b981', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>{saving ? 'กำลังบันทึก...' : (invoice ? 'บันทึก' : 'สร้างบิล')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
